import React from "react";
import { Col, Row, Button, Card, Alert } from "react-bootstrap";
import FontAwesome from "react-fontawesome";
import { Map } from "immutable";
import NumberedScoreScale from "components/DynamicScoreReportScales/NumberedScoreScale";
import MultipleScoreScales from "components/DynamicScoreReportScales/MultipleScoreScales";
import CTRSE from "./ScoreReportPage/TestReports/CTRSE";
import AverageScoreScale from "components/DynamicScoreReportScales/AverageScoreScale";
import { getMinMaxScoreRange } from "containers/ScoreRanges/helperFunctions";
import InRangeBadge from "resources/images/dynamic_score_reports/in-range-badge.png";
import TKTIcon from "resources/images/dynamic_score_reports/skills/score-report/tkt-icon.png";
import TTIcon from "resources/images/dynamic_score_reports/skills/score-report/tt-icon.png";
import ExcelIcon from "resources/images/dynamic_score_reports/skills/score-report/excel-icon.png";
import WordIcon from "resources/images/dynamic_score_reports/skills/score-report/word-icon.png";
import PPTIcon from "resources/images/dynamic_score_reports/skills/score-report/ppt-icon.png";
import CLIKIcon from "resources/images/dynamic_score_reports/skills/score-report/clik-icon.png";
import redWarningIcon from "resources/images/warning@3x.png";
import StrengthsIcon from "resources/images/dynamic_score_reports/personality/score-report/Strengths-icon.png";
import PotentialStrengthsIcon from "resources/images/dynamic_score_reports/personality/score-report/PotentialStrengths-icon.png";
import NeedsAttentionsIcon from "resources/images/dynamic_score_reports/personality/score-report/NeedsAttention-icon.png";
import AptitudeIcon from "resources/images/dynamic_score_reports/aptitude/score-report/aptitude-score-report-icon.png";
import PersonalityIcon from "resources/images/dynamic_score_reports/personality/score-report/personality-score-report-icon.png";
import EmotionalIntelligenceIcon from "resources/images/dynamic_score_reports/emotional/score-report/emotional-intelligence.png";
import SkillsIcon from "resources/images/dynamic_score_reports/skills/score-report/skills-score-report-icon.png";
import RiskIcon from "resources/images/dynamic_score_reports/risk/score-report/risk-score-report-icon.png";
import TestMakerIcon from "resources/images/dynamic_score_reports/testmaker/score-report/testmaker-score-report-icon.png";
import InfoIcon from "resources/images/info.svg";
import YellowWarningIcon from "resources/images/icon-16-px-warning@3x.png";
import RedWarningIconWhiteBg from "resources/images/red-warning-white-bg.png";
import ExtendedBadge from "resources/images/dynamic_score_reports/extended-badge.png";
import ColoredRawScoreScale from "components/DynamicScoreReportScales/ColoredRawScoreScale";
import {
  numberToOrdinalString,
  getTestClassName,
  testScoreRangeHasTextScores,
} from "utils/helperFunctions";
import { clptExpressSubTestIds } from "utils/helperConstants";
import { getScoreDisplay } from "containers/Results/FunctionLib";
import EPP from "./ScoreReportPage/TestReports/EPP";
import EmotifyCognifyUCognify from "./ScoreReportPage/TestReports/EmotifyCognifyUCognify";
import GAME from "./ScoreReportPage/TestReports/GAME";
import CPI from "./ScoreReportPage/TestReports/CPI";
import CLIK from "./ScoreReportPage/TestReports/CLIK";
import WPP from "./ScoreReportPage/TestReports/WPP";
import WSP from "./ScoreReportPage/TestReports/WSP";
import SalesAssessment from "./ScoreReportPage/TestReports/SalesAssessment";
import WAA from "./ScoreReportPage/TestReports/WAA";
import CASTandMRAB from "./ScoreReportPage/TestReports/CASTandMRAB";
import Illustrait from "./ScoreReportPage/TestReports/IllustraitReports/Illustrait";
import RawScoreTestReport from "./ScoreReportPage/TestReports/RawScoreTestReports/RawScoreTestReport";
import Office365 from "./ScoreReportPage/TestReports/Office365";
import CLPT from "./ScoreReportPage/TestReports/CLPT";
import { getTopFourWorkFactors } from "./ScoreReportPage/ScoreReportHelpers";
import {
  showInvalidTestWarning,
  getScoreRangeName,
  getIllustraitNoCompetencySetText,
  waaWithoutWorkFactorsText,
  capitalizeFirstLetterOfWords,
} from "./helpers";
import CandidateResponseSection from "./ScoreReportPage/TestReports/CandidateResponseReports/CandidateResponseSection";
import {
  testDetails,
  dynamicTraitFieldToPretty,
  testTypeScoreRangeIconMap,
  cognify3GameSubTestIds,
  ucognify2GameSubTestIds,
  testsWithScoreRangeSections,
  wppScoreTextMap,
} from "./constants";

/* ------------------------ TEST SUMMARY SECTIONS ------------------------  */

export const renderTestSummaryContent = (
  test,
  testId,
  supportedTest,
  handleViewDetailsButton,
  hideBaseline
) => {
  const testInfo = test.get("test");
  const reportInfo = test.get("report");
  const summaryScores = test.get("summary");
  const scoreRangeData = test.get("rangeData");
  const detailScores = reportInfo && (reportInfo.get("details") || reportInfo);
  const subTestId = detailScores && detailScores.get("subTestId");
  const workFactors = reportInfo && reportInfo.get("workFactors");
  const reportText = reportInfo && reportInfo.get("text");
  const translations = reportInfo && reportInfo.get("translations");

  // EPP only
  const positions = reportText && reportText.get("positions");
  const eppScoreRangeTraits =
    reportInfo && reportInfo.getIn(["eppData", "scoreRangeTraitRanges"]);

  /* --------- Score range data ---------  */
  const hasScoreRange = test.get("baseline").get("hasBaseline");
  const metScoreRange =
    !hideBaseline && hasScoreRange && test.get("baseline").get("inBaseline");
  const scoreRangeName = getScoreRangeName(scoreRangeData);
  const isCriteriaScoreRange = test.get("baseline").get("defaultBaseline");

  const SummaryHeader = getSummaryHeader(
    testId,
    testInfo,
    handleViewDetailsButton,
    supportedTest,
    metScoreRange,
    reportInfo && reportInfo.get("extended"),
    subTestId
  );

  if (!supportedTest) {
    return [SummaryHeader];
  }

  const ScoreBoxes = getSummaryScoreBoxes(
    testId, // Not parsing to number on purpose to account for testMaker testIds
    summaryScores,
    detailScores,
    hasScoreRange,
    translations,
    positions
  );

  const displayScoreRangeSection =
    hasScoreRange && testsWithScoreRangeSections.includes(Number(testId));

  const ScoreScaleSection = getScoreScaleSection(
    Number(testId),
    detailScores,
    hasScoreRange,
    scoreRangeData,
    displayScoreRangeSection,
    eppScoreRangeTraits,
    reportText
  );

  let ScoreRangeSection = null;
  if (displayScoreRangeSection) {
    ScoreRangeSection = getScoreRangeSection(
      scoreRangeData,
      scoreRangeName,
      testInfo && testInfo.get("testType"),
      testId,
      isCriteriaScoreRange
    );
  }

  const AdditionalText = getAdditionalTestSummaryText(
    testId,
    reportText,
    detailScores,
    workFactors
  );

  return [
    SummaryHeader,
    ScoreBoxes,
    ScoreScaleSection,
    ScoreRangeSection,
    AdditionalText,
  ];
};

const getSummaryHeader = (
  testId,
  testInfo,
  handleViewDetailsButton,
  supportedTest,
  metScoreRange,
  isExtended,
  subTestId
) => {
  let testNameToShow = testInfo.get("testName");
  let testAbbrevToShow = testInfo.get("testAbbreviation");

  // CLPT display logic
  if (clptExpressSubTestIds.includes(String(subTestId))) {
    testNameToShow += " Express - English";
    testAbbrevToShow = "CLPTE-EN";
  } else if (testId === "57") {
    testNameToShow += " - English";
  }

  return (
    <Row>
      <Col lg={1}>
        <div className="test-rectangle-card">
          <h6 className={getTestClassName("test-card-test-name", testId)}>
            {testAbbrevToShow}
          </h6>
        </div>
      </Col>
      <Col lg={6}>
        <p
          className="font-weight-bold mb-0"
          style={{
            color: "#02215a",
          }}
        >
          {testNameToShow}
        </p>
        <p>{testInfo.get("testDescription")}</p>
      </Col>
      {supportedTest && (
        <Col lg={5}>
          <Row className="d-flex justify-content-end pr-3">
            {isExtended && (
              <img src={ExtendedBadge} alt="Extended time" className="mr-2" />
            )}
            {metScoreRange && (
              <img
                src={InRangeBadge}
                alt="In Range"
                title="In Range"
                style={{ marginRight: "8px", height: "30px" }}
              />
            )}
            <span>
              <Button
                variant="outline-primary"
                size="sm"
                value={testId}
                onClick={handleViewDetailsButton}
                id="viewDetails"
              >
                View Details{" "}
                <FontAwesome name="arrow-right" style={{ marginLeft: "5px" }} />
              </Button>
            </span>
          </Row>
        </Col>
      )}
    </Row>
  );
};

const getSummaryScoreBoxes = (
  testId,
  summaryScores,
  detailScores,
  hasScoreRange,
  translations,
  positions
) => {
  const ScoreBoxes = [];

  // Tests that do not display score boxes
  if (
    ([29, 34].includes(Number(testId)) && !hasScoreRange) || // EPP with no score range
    Number(testId) === 13 // CPI
  ) {
    return ScoreBoxes;
  }

  const allSummaryScores = getAllSummaryScores(
    testId,
    summaryScores,
    detailScores,
    translations,
    positions
  );

  allSummaryScores.forEach((score, scoreName) => {
    ScoreBoxes.push(
      // eslint-disable-next-line react/no-array-index-key
      <Col key={scoreName} className={`summary-score-box ${scoreName}`}>
        <strong
          style={{ fontSize: String(score).length > 4 ? "20px" : "40px" }}
        >
          {score}
        </strong>
        <p>{scoreName}</p>
      </Col>
    );
  });

  return ScoreBoxes;
};

const getScoreScaleSection = (
  testId,
  detailScores,
  hasScoreRange,
  scoreRangeData,
  displayScoreRangeSection,
  eppScoreRangeTraits,
  reportText
) => {
  // If Illustrait has no competency set or WAA has no Work Factors, show an alert instead of scale
  if (
    (testId === 48 && detailScores.get("percentile") === null) ||
    (testId === 50 && !detailScores)
  ) {
    return getNoResultsAlert(testId, true);
  }

  const isMultipleTraitScoreRange =
    hasScoreRange && scoreRangeData.get("scoreRanges").size > 1;

  const scoreRange = hasScoreRange && scoreRangeData.get("scoreRanges").first();
  const scoreRangeType = scoreRange && scoreRange.get("traitAbbrev");

  if (displayScoreRangeSection) {
    // If score range has more than 1 trait range or the score range is for proficiency,
    // show message instead of scale(s).
    if (isMultipleTraitScoreRange || scoreRangeType === "proficiency") {
      return MultipleTraitScoreRangeMessage;
    }

    if (["raw", "percentile", "score"].includes(scoreRangeType) && testId !== 59) {
      return getSingleScoreRangeScale(
        detailScores,
        scoreRange,
        scoreRangeType,
        testId
      );
    }
  }

  switch (testId) {
    case 10: // CCAT
    case 13: // CPI
    case 14: // MRAB
    case 23: // CAST
    case 31: // WPP
    case 37: // UCAT
    case 42: // GAME
      return (
        <MultipleScoreScales
          testId={testId}
          scores={detailScores}
          scoreRange={scoreRange}
          scoreRangeType={scoreRangeType}
          maxScore={100}
        />
      );
    case 12: // CBST
      return (
        <MultipleScoreScales
          testId={testId}
          scores={detailScores}
          scoreRange={scoreRange}
          scoreRangeType={scoreRangeType}
          maxScore={20}
        />
      );
    case 34: // EPP
      return (
        <MultipleScoreScales
          testId={testId}
          scores={detailScores}
          maxScore={100}
          scoreRange={hasScoreRange ? eppScoreRangeTraits : null}
        />
      );
    case 44: // Emotify V1
    case 45: // Cognify
    case 46: // WSP
    case 48: // Illustrait
    case 51: // Emotify V2
    case 52: // UCognify
    case 59: // CTRSE
      return (
        <AverageScoreScale
          score={detailScores.get("percentile")}
          styleClass="candidate-summary"
          testId={testId}
        />
      );
    case 15: // CSAP
    case 24: // SalesAP
      return getSalesAssessmentBarChart(reportText);
    case 30:
    case 49: // CLIK
      return (
        <ColoredRawScoreScale
          testId={testId}
          score={detailScores.get("rawScore")}
          styleClass="candidate-summary"
        />
      );
    default:
      return null;
  }
};

const getSingleScoreRangeScale = (
  detailScores,
  scoreRange,
  scoreRangeType,
  testId
) => {
  let score = 0;

  switch (scoreRangeType) {
    case "raw": {
      score = detailScores.get("rawScore");
      break;
    }
    case "percentile": {
      score = detailScores.get("percentile");
      break;
    }
    case "score": {
      score = detailScores.get("stwenty"); // Office 365 tests
      break;
    }
    default:
      break;
  }

  const maxScore = Number(scoreRange.get("max_range"));
  const [min, max] = getMinMaxScoreRange(scoreRange, testId);

  return (
    <NumberedScoreScale
      score={score}
      maxScore={maxScore}
      cellSpan={scoreRangeType === "score" ? 4 : 10}
      hasScoreRange
      scoreRangeMin={Number(min)}
      scoreRangeMax={Number(max)}
    />
  );
};

const getScoreRangeLabel = (scoreRange, testId) => {
  let rangeType = scoreRange.get("scoreRangeAbbrev");

  if (
    rangeType === "Raw Score" &&
    testScoreRangeHasTextScores(testId, rangeType)
  ) {
    rangeType = "Recommendation";
  }

  const minMaxText = getMinMaxScoreRange(scoreRange, testId, true);

  return `${rangeType} ${minMaxText}`;
};

const getScoreRangeSection = (
  scoreRangeData,
  scoreRangeName,
  testType,
  testId,
  isCriteriaScoreRange
) => {
  if (!scoreRangeData || scoreRangeData.size === 0) {
    return null;
  }

  let scoreRangeCaption = "Suggested Score Range:";

  const scoreRanges = scoreRangeData.get("scoreRanges");
  const isMultipleTraitScoreRange = scoreRanges.size > 1;

  scoreRanges.forEach((scoreRange, index) => {
    scoreRangeCaption += ` ${getScoreRangeLabel(scoreRange, testId)}`;

    if (isMultipleTraitScoreRange && index !== scoreRanges.size - 1) {
      scoreRangeCaption += ";";
    }
  });

  return (
    <Row className="d-flex justify-content-start ml-2">
      <Col lg={2}>
        <img
          src={getScoreRangeIcon(isCriteriaScoreRange, testType)}
          alt="Score range icon"
          title="Score range icon"
          style={{ height: "64px" }}
        />
      </Col>
      <Col
        className="px-0"
        style={{ color: "#02215a", paddingTop: "12px", marginLeft: "-10px" }}
      >
        <strong>{scoreRangeName}</strong>
        <p>{scoreRangeCaption}</p>
      </Col>
    </Row>
  );
};

const getScoreRangeIcon = (isCriteriaScoreRange, testType) => {
  if (!isCriteriaScoreRange) {
    return testTypeScoreRangeIconMap[testType];
  }

  try {
    if (isCriteriaScoreRange) {
      // eslint-disable-next-line global-require
      const imgSrc = require(
        `resources/images/dynamic_score_reports/${testType}/score-range-icons/recommended-sr-${testType}.png`
      );
      return imgSrc.default;
    }
  } catch (error) {
    return testTypeScoreRangeIconMap[testType];
  }
};

export const getInvalidWarningRow = (testId) => (
  <Row
    className="text-danger font-weight-bold justify-content-center pt-3"
    style={{ fontSize: "16px" }}
  >
    <img className="mr-2" src={redWarningIcon} alt="warning" width="24px" />
    {testId === "48"
      ? "Caution: Confirmation of Results Needed"
      : "Warning: Invalid Result"}
  </Row>
);

const getSalesAssessmentBarChart = (reportText) => {
  if (!(reportText && reportText.get("summary") && reportText.get("traits"))) {
    return null;
  }

  // Class names are to hard just use the png's instead but still use the same function
  const counts = {
    Strengths: {
      count: 0,
      imgSrc: StrengthsIcon,
    },
    "Potential Strengths": {
      count: 0,
      imgSrc: PotentialStrengthsIcon,
    },
    "Needs Attention": {
      count: 0,
      imgSrc: NeedsAttentionsIcon,
    },
  };

  reportText.get("summary").forEach((traitVal) => {
    counts[traitVal].count += 1;
  });
  return (
    <table>
      <tbody>
        {Object.entries(counts).map(([label, { count, imgSrc }]) => {
          const strengthIcons = [...Array(count).keys()].map((i) => (
            <img
              src={imgSrc}
              key={`${i}_${label}`}
              alt={label}
              className="sales-strengths-icons mr-1"
            />
          ));

          return (
            <tr key={label}>
              <td>
                <p className="text-right mt-1">{label}</p>
              </td>
              <td className="d-flex ml-2">
                <div>{strengthIcons}</div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

/* ------------------------ TEST INFO SECTION  ------------------------  */

export const renderResultsSummaryBox = (
  testId,
  summaryScores,
  reportData,
  detailScores,
  translations,
  positions,
  selectedScoreRange
) => {
  const showInvalidWarning = showInvalidTestWarning(testId, reportData);
  if (testId === 13 && !showInvalidWarning) {
    return null;
  }

  const eppScoreRangeData = {
    selectedScoreRange,
    reportData,
  };

  const ResultBoxes = [];

  getAllSummaryScores(
    testId,
    summaryScores,
    detailScores,
    translations,
    positions,
    true,
    eppScoreRangeData
  ).forEach((score, scoreName) => {
    const addDivider = ResultBoxes.length >= 1;
    ResultBoxes.push(getResultBox(score, scoreName, addDivider));
  });

  const testWithBlueWarningHeader = [15, 24, 31, 46, 48].includes(testId);
  const isIllustrait = testId === 48;

  return (
    <Row>
      <Col className="mx-2">
        <Row
          style={{
            backgroundColor:
              showInvalidWarning && !testWithBlueWarningHeader
                ? "#ca003d"
                : "#19355f",
          }}
          className="results-summary-box-header"
        >
          {showInvalidWarning ? (
            <Col>
              <h2
                className="font-weight-bold mb-0"
                style={{
                  fontSize: testWithBlueWarningHeader ? "15px" : "21px",
                }}
              >
                {testWithBlueWarningHeader ? (
                  <img
                    src={RedWarningIconWhiteBg}
                    alt="Warning"
                    className="red-warning-icon-white-bg"
                  />
                ) : (
                  <FontAwesome name="exclamation-triangle" className="mr-2" />
                )}
                {isIllustrait
                  ? "Attention: Confirmation of Results Needed"
                  : "Warning: Invalid Result"}
              </h2>
              <p
                className="mb-0 mt-2"
                style={{
                  fontSize: testWithBlueWarningHeader ? "8.5px" : "13px",
                }}
              >
                {isIllustrait ? "See Validity & Response Style" : "SEE BELOW"}
              </p>
            </Col>
          ) : (
            <div>
              <h2
                className="font-weight-bold mb-0"
                style={{ fontSize: "21px" }}
              >
                Results Summary
              </h2>
            </div>
          )}
        </Row>
        <Row className="results-summary-row">{ResultBoxes}</Row>
      </Col>
    </Row>
  );
};

const getResultBox = (score, scoreName, addDivider) => {
  if (scoreName === "Job Family % Match") {
    return (
      <Col key={scoreName} className="jobFamilyMatch">
        <p>
          To view a job match score,
          <br /> select a job family below
        </p>
      </Col>
    );
  }

  return (
    <Col
      key={scoreName}
      className={`standardScoreCol ${scoreName} ${
        addDivider ? "withDivider" : ""
      }`}
    >
      <strong>{score}</strong>
      <p>{scoreName}</p>
    </Col>
  );
};

export const getTestTypeIcon = (testType, testId) => {
  switch (Number(testId)) {
    case 17:
      return <img src={TTIcon} alt="Typing Test" />;
    case 18:
      return <img src={TKTIcon} alt="Ten Key Test" />;
    case 21:
    case 32:
    case 35:
    case 39:
    case 53:
      return <img src={ExcelIcon} alt="Excel Test" />;
    case 22:
    case 33:
    case 36:
    case 40:
    case 54:
      return <img src={WordIcon} alt="Word Test" />;
    case 38:
    case 41:
    case 55:
      return <img src={PPTIcon} alt="PowerPoint Test" />;
    case 30:
    case 49:
      return <img src={CLIKIcon} alt="CLIK Test" />;
    default:
  }

  switch (testType) {
    case "aptitude":
      return <img src={AptitudeIcon} alt="Aptitude test" />;
    case "personality":
      return <img src={PersonalityIcon} alt="Personality test" />;
    case "skills":
      return <img src={SkillsIcon} alt="Skills test" />;
    case "testMaker":
      return <img src={TestMakerIcon} alt="TestMaker test" />;
    case "emotional":
      return <img src={EmotionalIntelligenceIcon} alt="Emotional test" />;
    case "risk":
      return <img src={RiskIcon} alt="Risk test" />;
    default:
      return null;
  }
};

export const getDynamicTestTypeBadgeClassName = (testType) => {
  switch (testType) {
    case "aptitude":
    case "personality":
    case "emotional":
    case "risk":
      return `${testType}-test-type-badge`;
    case "skills":
      return "dsr-skills-test-badge";
    default:
      return "testmaker-test-type-badge";
  }
};

/* ------------------------ TEST REPORTS  ------------------------  */

export const renderTestReport = (
  candidateInfo,
  initialScoreRange,
  invalidTest,
  reportInfo,
  scoreRangeData,
  testId,
  testInfo,
  hideBaseline,
  viewingAlternateTestTab,
  isInsightUser,
  isVistaUser,
  fromCandidateProfile,
  onCopyCandidateToJob,
  moveEmailCandidatesPermission,
  featureFlags,
  companyAccountId,
  pipelineStageName,
  isSampleJob,
  selectedScoreRange,
  setSelectedScoreRange,
  userData,
  hsVersion,
  onRedirectToDevelop
) => {
  const metScoreRange =
    testInfo.getIn(["baseline", "inBaseline"]) && !hideBaseline;
  const isCriteriaScoreRange = testInfo.getIn(["baseline", "defaultBaseline"]);
  const criteriaScoreRanges = scoreRangeData.get("positions");
  const customScoreRanges = scoreRangeData.get("testBaselines");
  const scoreRangeOptions = criteriaScoreRanges.merge(customScoreRanges);
  const testScores = reportInfo && (reportInfo.get("details") || reportInfo);
  const reportText = reportInfo && reportInfo.get("text");
  const testData = testInfo && testInfo.get("test");

  // If it is a testmaker test show the testmaker test report (could put this here or at the end of the default case for the switch)
  if (String(testId).substring(0, 2).toUpperCase() === "TM") {
    return (
      <CandidateResponseSection
        isTestMakerTT={isTestMakerTT(testId, testScores)}
        testId={testId}
        testScores={testScores}
      />
    );
  }

  const SubcategoryScoreColumns = getSubcategoryScoreColumns(
    Number(testId),
    testScores,
    reportText
  );

  const SelectedScoreRangeSection =
    initialScoreRange && !viewingAlternateTestTab
      ? getSelectedScoreRangeSection(
          scoreRangeData,
          initialScoreRange.rangeName,
          metScoreRange,
          isCriteriaScoreRange,
          testData && testData.get("testType"),
          testId
        )
      : null;

  switch (Number(testId)) {
    case 10:
    case 12:
    case 16:
    case 37:
    case 43:
      return (
        <RawScoreTestReport
          candidateInfo={candidateInfo}
          criteriaScoreRanges={criteriaScoreRanges}
          customScoreRanges={customScoreRanges}
          initialScoreRange={initialScoreRange}
          isInvalidTest={invalidTest}
          scoreRangeOptions={scoreRangeOptions}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          testScores={testScores}
          testId={Number(testId)}
          testInfo={testData}
        />
      );
    case 17:
    case 18:
    case 21:
    case 22:
    case 32:
    case 33:
    case 35:
    case 36:
    case 38:
    case 39:
    case 40:
    case 41:
      return (
        <CandidateResponseSection testId={testId} testScores={testScores} />
      );
    case 30:
    case 49:
      return <CLIK testId={Number(testId)} testScores={testScores} />;
    case 15:
    case 24:
      return (
        <SalesAssessment
          isInvalidTest={invalidTest}
          reportText={reportText}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          testScores={testScores}
          testId={Number(testId)}
        />
      );
    case 14:
    case 23:
      return (
        <CASTandMRAB
          testScores={testScores}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          testId={Number(testId)}
        />
      );
    case 42:
      return (
        <GAME
          candidateInfo={candidateInfo}
          testScores={testScores}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          scoreRangeOptions={scoreRangeOptions}
        />
      );
    case 13:
      return <CPI reportText={reportText} testScores={testScores} />;
    case 31:
      return (
        <WPP
          reportText={reportText}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          testScores={testScores}
        />
      );
    case 34:
      return (
        <EPP
          initialScoreRange={initialScoreRange}
          isInvalidTest={invalidTest}
          scoreRangeData={scoreRangeData}
          testInfo={testInfo}
          testScores={testScores}
          reportInfo={reportInfo}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          candidateInfo={candidateInfo}
          isInsightUser={isInsightUser}
          isVistaUser={isVistaUser}
          fromCandidateProfile={fromCandidateProfile}
          onCopyCandidateToJob={onCopyCandidateToJob}
          moveEmailCandidatesPermission={moveEmailCandidatesPermission}
          featureFlags={featureFlags}
          companyAccountId={companyAccountId}
          pipelineStageName={pipelineStageName}
          isSampleJob={isSampleJob}
          selectedScoreRange={selectedScoreRange}
          setSelectedScoreRange={setSelectedScoreRange}
          userData={userData}
          hsVersion={hsVersion}
          onRedirectToDevelop={onRedirectToDevelop}
        />
      );
    case 46:
      return (
        <WSP
          candidateInfo={candidateInfo}
          isInvalidTest={invalidTest}
          reportText={reportText}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          testScores={testScores}
        />
      );
    case 48:
      return (
        <Illustrait
          candidateInfo={candidateInfo}
          IllustraitWithoutCompetencySetAlert={getNoResultsAlert(48)}
          reportInfo={reportInfo}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          testScores={testScores}
        />
      );
    case 44:
    case 45:
    case 51:
    case 52:
      return (
        <EmotifyCognifyUCognify
          candidateInfo={candidateInfo}
          reportText={reportText}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          testId={Number(testId)}
          testScores={testScores}
        />
      );
    case 50:
      return (
        <WAA
          candidateInfo={candidateInfo}
          cDetails={reportInfo && reportInfo.get("cDetails")}
          reportText={reportText}
          testScores={testScores}
          workFactors={reportInfo && reportInfo.get("workFactors")}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          WaaWithoutWorkFactorsAlert={getNoResultsAlert(50)}
        />
      );
    case 53:
    case 54:
    case 55:
      return (
        <Office365
          candidateInfo={candidateInfo}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          testId={Number(testId)}
          testScores={testScores}
        />
      );
    case 57:
      return (
        <CLPT
          candidateInfo={candidateInfo}
          SelectedScoreRangeSection={SelectedScoreRangeSection}
          SubcategoryScoreColumns={SubcategoryScoreColumns}
          testId={Number(testId)}
          testScores={testScores}
        />
      );
      case 59:
        return (
          <CTRSE
            candidateInfo={candidateInfo}
            SelectedScoreRangeSection={SelectedScoreRangeSection}
            SubcategoryScoreColumns={SubcategoryScoreColumns}
            testId={Number(testId)}
            testScores={testScores}
          />
        );
    default:
      return null;
  }
};

const getSubcategoryScoreColumns = (testId, scores, text) => {
  if (!testDetails[testId]) {
    return [];
  }

  const ScoreColumns = [];
  testDetails[testId].subcategories.forEach((subcategory) => {
    let score = String(scores.get(subcategory));

    if (["undefined", "null"].includes(score)) {
      return null;
    }

    // Possible additional subscore to display in score column
    let additionalScoreName = null;

    switch (subcategory) {
      case "validityINC":
      case "validityENH":
        score = score === "1" ? "VALID" : "INVALID";
        break;
      case "readingScore":
        additionalScoreName = "readingProficiency";
        break;
      case "writingScore":
        additionalScoreName = "writingProficiency";
        break;
      case "listeningScore":
        additionalScoreName = "listeningProficiency";
        break;
      default:
        break;
    }

    const additionalScore = additionalScoreName
      ? getScoreDisplay(
          scores.get(additionalScoreName),
          additionalScoreName,
          String(testId)
        )
      : null;

    // contains descriptions and imgPaths for subcategories if needed
    const hasSubcategoryConstants = testDetails[testId].subcategoryConstants;
    const hasRichText =
      hasSubcategoryConstants && hasSubcategoryConstants.hasRichText;

    const subcategoryDescription = getSubcategoryDescription(
      testId,
      subcategory,
      hasSubcategoryConstants,
      scores,
      text
    );

    ScoreColumns.push(
      <Col
        xs={hasSubcategoryConstants ? null : 6}
        style={{ paddingRight: "36px", marginBottom: "24px" }}
        key={subcategory}
      >
        {hasSubcategoryConstants && (
          <Row className="d-flex justify-content-center mb-3">
            <img
              src={
                testDetails[testId].subcategoryConstants[subcategory].imgPath
              }
              alt="Subcategory score icon"
              width={80}
              height={80}
            />
          </Row>
        )}
        <Row
          className="criteria-navy-color traitDescriptionBox"
          style={{ height: "72px", flexWrap: "nowrap" }}
        >
          <Col
            xs={8}
            className={`d-flex flex-column ${
              additionalScore ? "pt-1" : "justify-content-center"
            }`}
          >
            <h3
              className="mb-0 font-weight-bold"
              style={{
                fontSize: "16px",
              }}
            >
              {dynamicTraitFieldToPretty[testId][subcategory]}
              {additionalScore && ":"}
            </h3>
            {additionalScore && (
              <p style={{ fontSize: "16px" }}>{additionalScore}</p>
            )}
          </Col>
          <Col
            className="text-right pr-4"
            style={{ marginTop: score.length > 4 ? "5px" : "0" }}
          >
            <span
              className="font-weight-bold"
              style={{
                whiteSpace: "nowrap",
                fontSize: score.length > 4 ? "24px" : "32px",
              }}
            >
              {score}
            </span>
          </Col>
        </Row>
        <Row>
          {!hasSubcategoryConstants || hasRichText ? (
            <p
              className="mt-3"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: subcategoryDescription,
              }}
            />
          ) : (
            <p className="mt-3">{subcategoryDescription}</p>
          )}
          {text && text.get("validity_text_2") && (
            <Alert variant="danger">
              <p
                className="mb-0"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: text.get("validity_text_2"),
                }}
              />
            </Alert>
          )}
        </Row>
      </Col>
    );
  });

  return ScoreColumns;
};

const getSubcategoryDescription = (
  testId,
  subcategory,
  hasSubcategoryConstants,
  scores,
  text
) => {
  if (hasSubcategoryConstants) {
    const constantSubcategoryDescription =
      testDetails[testId].subcategoryConstants[subcategory].description;

    const subTestId = scores.get("subTestId");

    if (testId === 45) {
      const isCognify3Game = cognify3GameSubTestIds.includes(subTestId);
      return isCognify3Game
        ? constantSubcategoryDescription["3game"]
        : constantSubcategoryDescription["6game"];
    }
    if (testId === 52) {
      const isUCognify2Game = ucognify2GameSubTestIds.includes(subTestId);
      return isUCognify2Game
        ? constantSubcategoryDescription["2game"]
        : constantSubcategoryDescription["5game"];
    }

    return constantSubcategoryDescription;
  }

  if (subcategory === "INC_score" || subcategory === "FAKING_percentile") {
    if ([31, 34].includes(testId)) {
      return text.get("validity_text");
    }
    if ([15, 24].includes(testId)) {
      return text.get("RSV_text");
    }
  }

  // WSP only
  if (subcategory === "validityINC") {
    return text.get("validity_INC_text");
  }
  if (subcategory === "validityENH") {
    return text.get("validity_ENH_text");
  }

  return text.get(`${subcategory.slice(0, 3)}_text`);
};

const getSelectedScoreRangeSection = (
  scoreRangeData,
  scoreRangeName,
  scoreRangeMet,
  isCriteriaScoreRange,
  testType,
  testId
) => {
  if (!(scoreRangeData && scoreRangeData.get("scoreRanges"))) {
    return null;
  }

  let scoreRangeCaption = "";
  scoreRangeData.get("scoreRanges").forEach((scoreRangeObj) => {
    scoreRangeCaption += `${getScoreRangeLabel(scoreRangeObj, testId)}\n`;
  });
  scoreRangeCaption = scoreRangeCaption.trim();

  const scoreRangeGreyText = isCriteriaScoreRange
    ? "Criteria Score Range"
    : "Custom Score Range";

  return (
    <div className="score-range-display-row">
      <div>
        <img
          src={getScoreRangeIcon(isCriteriaScoreRange, testType)}
          className="score-range-icon"
          alt="Score range icon"
          title="Score range icon"
        />
        <div className="score-range-display-text">
          <span className="grey-text">{scoreRangeGreyText}</span>
          <span className="bold-text">{scoreRangeName}</span>
        </div>
      </div>
      <div>
        <div className="score-range-display-text">
          <span className="grey-text">Parameters</span>
          <span className="parameters">{scoreRangeCaption}</span>
        </div>
      </div>
      <div>
        {scoreRangeMet && (
          <img
            src={InRangeBadge}
            alt="In Range"
            title="In Range"
            className="in-range-badge"
          />
        )}
      </div>
    </div>
  );
};

/* ------------------------ ADDITIONAL HELPERS  ------------------------  */

export const renderDownloadButton = (
  handleDownloadPdf,
  testId,
  reportType,
  linkStyle,
  testName
) => (
  <Button
    variant={linkStyle ? "link" : "outline-primary"}
    size="sm"
    onClick={() => handleDownloadPdf(testId, reportType, testName)}
    id={`download${reportType}`}
    style={{
      height: "30px",
      color: "#425cc7",
      fontWeight: linkStyle ? "bold" : "normal",
    }}
  >
    <FontAwesome name="download" className="mr-1" /> Download Report
    {reportType === "Candidate Summary" && "s"}
  </Button>
);

const convertTKTFields = (summaryScores, detailScores) => {
  const percentile = summaryScores.get("Percentile");
  const KPH = Number(summaryScores.get("KPH"));

  const typingErrors = Number(detailScores.get("typingErrors"));
  const fieldAccuracyRate = ((20 - typingErrors) / 20) * 100;

  return new Map({
    Percentile: percentile,
    "Keystrokes per Hour": KPH,
    "Field Accuracy Rate": `${fieldAccuracyRate}%`,
  });
};

const getAllSummaryScores = (
  testId,
  summaryScores,
  detailScores,
  translations,
  positions,
  fromSummarySection,
  eppScoreRangeData
) => {
  if (isTestMakerTT(testId, detailScores)) {
    return new Map({
      WPM: detailScores.get("wpm"),
      Errors: detailScores.get("typingErrors"),
      "Adjusted WPM": detailScores.get("adjustedWpm"),
    });
  }

  const noResultsAvailableMap = new Map({
    "": "No Results Available",
  });

  // Right now, just applies to WAA with no Work Factors
  if (!summaryScores) {
    return noResultsAvailableMap;
  }

  let selectedScoreRange = null;
  let reportData = null;

  if (eppScoreRangeData) {
    ({ selectedScoreRange, reportData } = eppScoreRangeData);
  }

  const showEPPJobFamilyMatch =
    testId === 34 &&
    selectedScoreRange &&
    selectedScoreRange.rangeName &&
    positions &&
    positions.get(selectedScoreRange.rangeName) &&
    positions.get(selectedScoreRange.rangeName).get("title");

  const eppJobFamilyMatchScore =
    showEPPJobFamilyMatch &&
    getEPPJobFamilyMatchScore(reportData, selectedScoreRange);

  // For some tests, only one score will be in summaryScores, so we only need the first values.
  const firstScoreName = showEPPJobFamilyMatch
    ? selectedScoreRange.rangeName
    : summaryScores.keySeq().first();

  const firstScore = showEPPJobFamilyMatch
    ? eppJobFamilyMatchScore
    : summaryScores.first();

  if (firstScoreName === "BLANK") {
    return getSummaryScoresWithoutName(
      testId,
      firstScore,
      fromSummarySection,
      translations,
      detailScores,
      noResultsAvailableMap
    );
  }

  return getSummaryScoresWithName(
    testId,
    detailScores,
    summaryScores,
    showEPPJobFamilyMatch,
    positions,
    firstScoreName,
    firstScore
  );
};

const isTestMakerTT = (testId, detailScores) =>
  String(testId).substring(0, 2).toUpperCase() === "TM" &&
  detailScores &&
  !isNaN(detailScores.get("adjustedWpm"));

const getAdditionalTestSummaryText = (
  testId,
  reportText,
  detailScores,
  workFactors,
  candidateFirstName
) => {
  switch (Number(testId)) {
    case 13: {
      if (!(reportText && reportText.get("translatedSnapshots"))) {
        return null;
      }

      return (
        <div>
          <div>
            <h5 className="sr-h5">Snapshot</h5>
          </div>
          <ul>
            {reportText.get("translatedSnapshots").map((snapShot, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{snapShot}</li>
            ))}
          </ul>
        </div>
      );
    }
    case 50: {
      if (!workFactors || !detailScores) {
        return null;
      }

      const mappedWorkFactors = Map(
        workFactors.map((wf) => [wf.get("column"), wf])
      );

      return (
        <div className="ml-2">
          <h5 className="sr-h5">Results Details</h5>
          {getTopFourWorkFactors(
            detailScores,
            candidateFirstName,
            mappedWorkFactors
          )}
        </div>
      );
    }
    default:
      return null;
  }
};

const MultipleTraitScoreRangeMessage = (
  <Col style={{ marginLeft: "100px", marginTop: "-16px" }}>
    <Row>
      <img
        src={InfoIcon}
        alt="Info"
        width="16px"
        height="16px"
        className="mr-1"
        style={{ marginTop: "1px" }}
      />
      <span className="font-italic" style={{ color: "#53565a" }}>
        See Score Report for more score range details.
      </span>
    </Row>
  </Col>
);

const getNoResultsAlert = (testId, fromSummary) => (
  <Col
    style={{
      marginTop: fromSummary ? "-15px" : "15px",
      marginBottom: fromSummary ? "" : "15px",
    }}
  >
    <Card
      style={{
        backgroundColor: "#fff5e5",
        border: "1px solid #ffedcc",
      }}
    >
      <Card.Body className="py-3">
        <Row>
          <Col lg={1}>
            <img
              src={YellowWarningIcon}
              alt="Warning"
              style={{
                width: "16px",
                height: "16px",
                marginTop: "-4.5px",
              }}
            />
          </Col>
          <Col className="px-1" style={{ color: "#805100" }}>
            {testId === 48 && getIllustraitNoCompetencySetText(fromSummary)}
            {testId === 50 && waaWithoutWorkFactorsText}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  </Col>
);

const getEPPJobFamilyMatchScore = (reportData, selectedScoreRange) => {
  const scoreRangeName = selectedScoreRange.rangeName.replace(/[^a-zA-Z]/g, "");

  return (
    reportData &&
    reportData.get("eppData") &&
    reportData.get("eppData").get("jobMatches").get(scoreRangeName)
  );
};

const getSummaryScoresWithoutName = (
  testId,
  firstScore,
  fromSummarySection,
  translations,
  detailScores,
  noResultsAvailableMap
) => {
  switch (Number(testId)) {
    case 15:
      return new Map({
        "for a Customer Service Role": capitalizeFirstLetterOfWords(firstScore),
      });
    case 24:
      return new Map({
        "for a Sales Role": capitalizeFirstLetterOfWords(firstScore),
      });
    case 31:
      if (fromSummarySection) {
        return new Map({
          [`${translations.get(wppScoreTextMap[firstScore])}.`]:
            capitalizeFirstLetterOfWords(firstScore),
        });
      }
      return new Map({
        "Overall Rating": capitalizeFirstLetterOfWords(firstScore),
      });
    case 34:
      return new Map({
        "Job Family % Match": firstScore,
      });
    case 30:
    case 49:
      return new Map({
        [fromSummarySection ? "Overall Score" : "Overall Rating"]: firstScore,
      });
    case 44:
    case 45:
    case 46:
    case 48:
    case 50:
    case 51:
    case 52:
    case 59:  
      if (detailScores.get("percentile") !== null) {
        return new Map({
          [firstScore]: `${numberToOrdinalString(
            detailScores.get("percentile")
          )} Percentile`,
        });
      }
      return noResultsAvailableMap;
    default:
      return noResultsAvailableMap;
  }
};

const getSummaryScoresWithName = (
  testId,
  detailScores,
  summaryScores,
  showEPPJobFamilyMatch,
  positions,
  firstScoreName,
  firstScore
) => {
  switch (Number(testId)) {
    case 17:
      return new Map({
        WPM: detailScores.get("wpm"),
        Errors: detailScores.get("typingErrors"),
        "Adjusted WPM": summaryScores.get("Adjusted WPM"),
        Percentile: summaryScores.get("Percentile"),
      });
    case 18:
      return convertTKTFields(summaryScores, detailScores);
    case 34: {
      const positionTitle = showEPPJobFamilyMatch
        ? positions.get(firstScoreName).get("title")
        : positions.get(firstScoreName.slice(0, -8)).get("title");

      return new Map({
        [positionTitle]: `${firstScore}%`,
      });
    }
    case 14:
    case 23:
    case 42:
      return new Map({
        "Overall Score Percentile": numberToOrdinalString(firstScore),
      });
    default:
      // If all fails just return summaryScores
      return summaryScores;
  }
};
